import React, { useEffect, useId, useRef, useState } from 'react';
import { t } from '@lingui/macro';

import KEYS from '@rover/constants/src/platform/keys.constants';
import DropdownSmall from '@rover/icons/svg/dropdown-small.svg';
import { Box, Flex } from '@rover/kibble/core';
import useTheme from '@rover/kibble/themes/useTheme';
import { emitAnalyticsEvent } from '@rover/rsdk/src/modules/Analytics';
import type { ServiceTypeOption, ServiceTypeSlug } from '@rover/types/src/ServiceType';

import focusOnElement from '../../../utilities/focusOnElement';
import { getServiceTypeIcon } from '../../StickyFiltersBar/utils';
import TruncatedText from '../../TruncatedText';
import { SearchDesktopUpdateServiceType } from '../analyticsEvents';

import FilterDropdown from './FilterDropdown';
import FilterSection from './FilterSection';
import ServiceTypeDropdown from './ServiceTypeDropdown';

type ServiceTypeSectionProps = {
  serviceType: ServiceTypeOption | null;
  serviceTypeOptions: ServiceTypeOption[];
  onServiceTypeChange: (newFilters: { serviceType: ServiceTypeSlug }) => void;
  isDropdownOpen: boolean;
  setIsDropdownOpen: (value: boolean) => void;
};

const ServiceTypeSection = ({
  serviceType,
  serviceTypeOptions,
  onServiceTypeChange,
  isDropdownOpen,
  setIsDropdownOpen,
}: ServiceTypeSectionProps): JSX.Element => {
  const theme = useTheme();
  const [focusedIndex, setFocusedIndex] = useState<number>(-1);
  const sectionRef = useRef<HTMLElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const triggerId = useId();
  const dropdownId = useId();
  const getOptionId = (index: number): string => `${dropdownId}-option-${index}`;

  const serviceTypeValue = serviceType?.title || t`Boarding`;
  const serviceTypeSubtitle = serviceType?.subtitle || '';
  const serviceTypeSlug = serviceType?.value;
  const serviceIcon = getServiceTypeIcon(serviceTypeSlug, theme.colors.brand.active_brand);
  const currentServiceType = serviceType || serviceTypeOptions[0];

  // Initialize focused index to current selection when dropdown opens
  useEffect(() => {
    if (isDropdownOpen) {
      const selectedIndex = serviceTypeOptions.findIndex((opt) => opt.value === serviceTypeSlug);
      setFocusedIndex(selectedIndex === -1 ? 0 : selectedIndex);

      focusOnElement(dropdownRef);
    } else {
      // Reset focus index when closed
      setFocusedIndex(-1);
    }
  }, [isDropdownOpen, serviceTypeSlug, serviceTypeOptions]);

  const handleSectionClick = (): void => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleChange = (value: ServiceTypeSlug): void => {
    // Only track if service type actually changed
    if (value !== serviceTypeSlug) {
      emitAnalyticsEvent(new SearchDesktopUpdateServiceType({ serviceType: value }));
    }

    onServiceTypeChange({ serviceType: value });
    setIsDropdownOpen(false);

    focusOnElement(sectionRef);
  };

  const handleClose = (): void => {
    setIsDropdownOpen(false);

    focusOnElement(sectionRef);
  };

  const handleKeyDown = (event: React.KeyboardEvent): void => {
    switch (event.key) {
      case KEYS.DOWN:
        event.preventDefault();
        setFocusedIndex((prev) => (prev < serviceTypeOptions.length - 1 ? prev + 1 : prev));
        break;
      case KEYS.UP:
        event.preventDefault();
        setFocusedIndex((prev) => (prev > 0 ? prev - 1 : prev));
        break;
      case KEYS.ENTER:
      case KEYS.SPACE:
        event.preventDefault();
        if (focusedIndex >= 0 && focusedIndex < serviceTypeOptions.length) {
          handleChange(serviceTypeOptions[focusedIndex].value as ServiceTypeSlug);
        }
        break;
      default:
        break;
    }
  };

  return (
    <Box position="relative" flex="1" height="100%">
      <FilterSection
        onClick={handleSectionClick}
        ref={sectionRef}
        isOpen={isDropdownOpen}
        id={triggerId}
        aria-label={t`Service type: ${serviceTypeValue}`}
        aria-controls={dropdownId}
      >
        <Flex alignItems="center" gap="2x" width="100%">
          {serviceIcon && <Box flexShrink="0">{serviceIcon}</Box>}
          <Flex flexDirection="column" flex="1" minWidth="0">
            <TruncatedText size="100" fontWeight="semibold" textColor="primary" textAlign="left">
              {serviceTypeValue}
            </TruncatedText>
            {serviceTypeSubtitle && (
              <TruncatedText size="100" textColor="tertiary" textAlign="left">
                {serviceTypeSubtitle}
              </TruncatedText>
            )}
          </Flex>
          <Box flexShrink="0">
            <DropdownSmall width={24} height={24} color={theme.colors.text.primary} />
          </Box>
        </Flex>
      </FilterSection>

      {isDropdownOpen && (
        <FilterDropdown
          ref={dropdownRef}
          onClose={handleClose}
          triggerRef={sectionRef}
          id={dropdownId}
          role="listbox"
          aria-labelledby={triggerId}
          aria-activedescendant={focusedIndex >= 0 ? getOptionId(focusedIndex) : undefined}
          onKeyDown={handleKeyDown}
        >
          <ServiceTypeDropdown
            options={serviceTypeOptions}
            value={currentServiceType.value}
            onChange={handleChange}
            iconColor={theme.colors.brand.active_brand}
            focusedIndex={focusedIndex}
            getOptionId={getOptionId}
          />
        </FilterDropdown>
      )}
    </Box>
  );
};

export default ServiceTypeSection;
